// Shared reports data hook — fetches the four real report endpoints and
// shapes them for both the native and web Reports screens. No mock data:
// every value here comes from the backend (or is null/empty until loaded).
import { useEffect, useState } from 'react';
import { reportsService } from '../services/reports/reports.service';

export type ReportRange = '7d' | '30d' | '90d';

export interface ReportsData {
  loading: boolean;
  error: boolean;
  volume: Array<{ label: string; value: number }>;
  totalCreated: number;
  totalResolved: number;
  csatMean: number | null;
  csatTotal: number;
  csatDistribution: Record<string, number>;
  slaFirstResponseRate: number | null;
  slaResolutionRate: number | null;
  agents: Array<{ platformUserId: string; resolved: number; avgMinutesToResolve: number | null }>;
}

const EMPTY: ReportsData = {
  loading: true, error: false,
  volume: [], totalCreated: 0, totalResolved: 0,
  csatMean: null, csatTotal: 0, csatDistribution: {},
  slaFirstResponseRate: null, slaResolutionRate: null,
  agents: [],
};

function rangeWindow(range: ReportRange): { from: string; to: string; groupBy: 'day' | 'week' } {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const to = new Date();
  const from = new Date(to.getTime() - days * 86400000);
  return { from: from.toISOString(), to: to.toISOString(), groupBy: days > 31 ? 'week' : 'day' };
}

function bucketLabel(iso: string, groupBy: 'day' | 'week'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function useReportsData(range: ReportRange): ReportsData {
  const [data, setData] = useState<ReportsData>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    setData(d => ({ ...d, loading: true, error: false }));
    const { from, to, groupBy } = rangeWindow(range);

    Promise.allSettled([
      reportsService.volume(from, to, groupBy),
      reportsService.csat(from, to),
      reportsService.slaAttainment(from, to),
      reportsService.agentProductivity(from, to),
    ]).then(([vol, csat, sla, agents]) => {
      if (cancelled) return;
      const next: ReportsData = { ...EMPTY, loading: false };

      if (vol.status === 'fulfilled') {
        const rows = (vol.value as any)?.data ?? [];
        next.volume = rows.map((r: any) => ({ label: bucketLabel(r.bucket, groupBy), value: Number(r.created) || 0 }));
        next.totalCreated = rows.reduce((s: number, r: any) => s + (Number(r.created) || 0), 0);
        next.totalResolved = rows.reduce((s: number, r: any) => s + (Number(r.resolved) || 0), 0);
      } else { next.error = true; }

      if (csat.status === 'fulfilled') {
        next.csatMean = (csat.value as any)?.mean ?? null;
        next.csatTotal = Number((csat.value as any)?.total ?? 0);
        next.csatDistribution = (csat.value as any)?.distribution ?? {};
      }

      if (sla.status === 'fulfilled') {
        const v: any = sla.value;
        next.slaFirstResponseRate = v?.firstResponse?.rate ?? v?.first_response?.rate ?? v?.first_response?.attainment ?? null;
        next.slaResolutionRate = v?.resolution?.rate ?? v?.resolution?.attainment ?? null;
      }

      if (agents.status === 'fulfilled') {
        next.agents = ((agents.value as any)?.data ?? []).map((a: any) => ({
          platformUserId: a.platformUserId,
          resolved: Number(a.resolved) || 0,
          avgMinutesToResolve: a.avgMinutesToResolve ?? null,
        }));
      }

      setData(next);
    });

    return () => { cancelled = true; };
  }, [range]);

  return data;
}
